import { global, getSiteAbsoluteURL } from "../Common/saop-common";
import { SaopMessageDlg, SaopMsgDlgButton } from '../Components/saop-message-dlg';
import { SaopServicesCore } from "./saop-services-core";

type Lang = "sl" | "en" | "sr" | "hr"; 
function toLang(value: string | null | undefined): Lang {
  const v = (value || "").trim().toLowerCase();
  return v === "en" ? "en" : v === "sr" ? "sr" : v === "hr" ? "hr" : "sl";
}

// prevodi so hardkodirani, ker možen klic translation servica pred avtentikacijo sproži napako
const translations: Record<Lang, Record<string, string>> =  {
    sl: {
        "Vaša seja bo kmalu potekla": "Vaša seja bo kmalu potekla",
        "Vaša seja bo potekla čez": "Vaša seja bo potekla čez",
        "Ali želite nadaljevati z delom?": "Ali želite nadaljevati z delom?",
        "Vaša seja je potekla": "Vaša seja je potekla",
        "Za nadaljevanje se je potrebno ponovno prijaviti.": "Za nadaljevanje se je potrebno ponovno prijaviti.",
        "Preusmerjeni boste na prijavno stran.": "Preusmerjeni boste na prijavno stran.",
    },
    sr: {
        "Vaša seja bo kmalu potekla": "Vaša sesija će uskoro isteći",
        "Vaša seja bo potekla čez": "Vaša sesija ističe za",
        "Ali želite nadaljevati z delom?": "Da li želite da nastavite sa radom?",
        "Vaša seja je potekla": "Vaša sesija je istekla",
        "Za nadaljevanje se je potrebno ponovno prijaviti.": "Za nastavak je potrebno da se ponovo prijavite.",
        "Preusmerjeni boste na prijavno stran.": "Bićete preusmereni na stranicu za prijavu.",
    },
    en: {
        "Vaša seja bo kmalu potekla": "Your session will expire soon",
        "Vaša seja bo potekla čez": "Your session will expire in",
        "Ali želite nadaljevati z delom?": "Do you want to continue working?",
        "Vaša seja je potekla": "Your session has expired",
        "Za nadaljevanje se je potrebno ponovno prijaviti.": "You need to log in again to continue.",
        "Preusmerjeni boste na prijavno stran.": "You will be redirected to the login page.",
    },
    hr: {
        "Vaša seja bo kmalu potekla": "Vaša sesija će uskoro isteći",
        "Vaša seja bo potekla čez": "Vaša sesija istječe za",
        "Ali želite nadaljevati z delom?": "Želite li nastaviti s radom?",
        "Vaša seja je potekla": "Vaša sesija je istekla",
        "Za nadaljevanje se je potrebno ponovno prijaviti.": "Za nastavak se potrebno ponovno prijaviti.",
        "Preusmerjeni boste na prijavno stran.": "Bit ćete preusmjereni na stranicu za prijavu.",
    },
};


export interface SessionConfig {
    timeoutSeconds: number;
    warningSeconds: number;
}

export class SaopSessionWatchdog {
    private readonly STORAGE_KEY = 'saop_session_expires_at';
    private started = false;
    private warned = false;
    private expired = false;

    private expiresAtMs = 0;
    private tickHandle: number | null = null;

    private minutesToMs:number = 60_000;

    private idleTimeoutMs: number = 10 * this.minutesToMs;
    private warnBeforeMs: number = 3 * this.minutesToMs;

    private ableToRefresh: number = 5 * this.minutesToMs;

    private keepAliveInFlight: boolean = false;

    private lastActivityMs = 0;
    private lastActivityWindow: number = 1 * this.minutesToMs;

    private saopMessageDlg: SaopMessageDlg;
    private _saopServicesCore: SaopServicesCore;
    private currentLanguage: Lang = "sl";
    

    constructor(options: SessionConfig) {
        // v Startup.cs na tak način zaokrožimo iz sekund v min, zato je tukaj isto
        this.idleTimeoutMs = ((options.timeoutSeconds <= 0) ? 10 : Math.floor(options.timeoutSeconds / 60)) * this.minutesToMs;
        this.warnBeforeMs = ((options.warningSeconds <= 0) ? 3 : Math.floor(options.warningSeconds / 60)) * this.minutesToMs;

        // sliding expiration na backendu resetira uro, samo ko mine že več kot pol nastavljenega okna
        this.ableToRefresh = this.idleTimeoutMs / 2 + 1_000;

        if (this.warnBeforeMs >= this.idleTimeoutMs) {
            this.warnBeforeMs = Math.floor(this.idleTimeoutMs / 2);
        }

    }

    public init(): void {
        global.ConsoleLogDegug("SaopSession.init");
        if (this.started) return;

        const path = (window.location.pathname || "").toLowerCase();
        if (
            path.includes("/account/") ||
            path.includes("/twofaauth/") ||
            path.includes("/saopuporabniki/") ||
            path.includes("/settings/changepasswordonlogin")
            ) {
            return;
            }

        this.saopMessageDlg = new SaopMessageDlg({ data: null, parent: null })
        this._saopServicesCore = new SaopServicesCore();

        this.started = true;

        const now = Date.now();
        this.expiresAtMs = now + this.idleTimeoutMs;
        this.lastActivityMs = now; 

        const raw = (document.getElementById("siteLanguage") as HTMLInputElement | null)?.value;

        this.currentLanguage = toLang(raw);

        this.bindActivityListeners();
        this.bindStorageListener(); 
        this.startTicker();
    }

    private bindStorageListener(): void {
        window.addEventListener('storage', (event) => {
            if (event.key === this.STORAGE_KEY) {
                this.silentLocalReset();
            }
        });
    }
    private tr(key: string) {
        return translations[this.currentLanguage]?.[key] ?? key;
    }

    private startTicker(): void {
        this.tickHandle = window.setInterval(() => this.tick(), 10000);
    }

    private stopTicker(): void {
        if (this.tickHandle != null) {
        window.clearInterval(this.tickHandle);
        this.tickHandle = null;
        }
    }

    private restartTicker(): void {
        this.stopTicker();
        this.startTicker();
    }

    private tick(): void {
        const now = Date.now();
        const remainingMs = this.expiresAtMs - now;

        if (remainingMs <= 0 && !this.expired) {
            this.expired = true;
            this.stopTicker();
            this.showExpired();
            return;
        }

        if (!this.warned && remainingMs <= this.warnBeforeMs) {
            this.warned = true;
            this.showWarning();
        }

        const userRecentlyActive = (now - this.lastActivityMs) <= this.lastActivityWindow;

        if (!this.warned && !this.expired && !this.keepAliveInFlight &&
            userRecentlyActive && remainingMs <= this.ableToRefresh) {
            this.refreshSession();
        }
    }

    private silentLocalReset(): void {
        global.ConsoleLogDegug("SaopSession.SilentLocalReset");

        const raw = localStorage.getItem(this.STORAGE_KEY);
        const parsed = raw ? parseInt(raw, 10) : NaN;

        this.expiresAtMs = !isNaN(parsed) ? parsed : Date.now() + this.idleTimeoutMs;
        this.lastActivityMs = Date.now();
        this.restartTicker();
            
        if (this.warned) {
            this.warned = false;
            if (this.saopMessageDlg && this.saopMessageDlg.IsVisible()) {
                this.saopMessageDlg.Close();
            }
        }
        this.expired = false;
    }

    private refreshSession(): void {
        global.ConsoleLogDegug("SaopSession.refreshSession");
        const now = Date.now();
        this.keepAliveInFlight = true;
        this.lastActivityMs = now;

        let _this = this;
        this._saopServicesCore.afterSuccess = function () {
            _this.warned = false; 
            _this.expired = false;
            _this.alignTimers();
        }
        this._saopServicesCore.afterError = function () {
            _this.keepAliveInFlight = false
        }

        this._saopServicesCore.ExecuteAction({
            action: "Session/KeepAlive",
            type: "POST"
        });

    }
    // dodaten klic na endpoint, ker komaj naslednji klic po refreshu vrne prave posodobljene čase
    private alignTimers(): void {
        global.ConsoleLogDegug("SaopSession.alignTimers");
        let _this = this;
        this._saopServicesCore.afterSuccess = function (response) {
            const serverNowMs = new Date(response.data.serverUtc).getTime();
            const expiresAtMs = new Date(response.data.expiresUtc).getTime();
            const remainingMs = expiresAtMs - serverNowMs;
            _this.expiresAtMs = Date.now() + remainingMs;
            localStorage.setItem(_this.STORAGE_KEY, _this.expiresAtMs.toString());

            _this.keepAliveInFlight = false
            _this.restartTicker();
        }
        this._saopServicesCore.afterError = function () {
            _this.expiresAtMs = Date.now() + _this.idleTimeoutMs;
            localStorage.setItem(_this.STORAGE_KEY, _this.expiresAtMs.toString());

            _this.keepAliveInFlight = false
        }

        this._saopServicesCore.ExecuteAction({
            action: "Session/AlignTimers",
            type: "POST"
        });
    }

    private bindActivityListeners(): void {
        const handler = () => {
            if (this.warned || this.expired) return;
            const now = Date.now();
            if (now - this.lastActivityMs < 1000) return;

            this.lastActivityMs = now;
        };
        
        window.addEventListener("mousemove", handler, { passive: true });
        window.addEventListener("keydown", handler, { passive: true });
        window.addEventListener("click", handler, { passive: true });
        window.addEventListener("scroll", handler, { passive: true });
    }

    showWarning(): void {
        global.ConsoleLogDegug("SaopSession.showExpired");
        let _this = this;

        let title = this.tr("Vaša seja bo kmalu potekla"); 

        let message = _this.tr("Vaša seja bo potekla čez") + " <b>" + Math.round(this.warnBeforeMs / 60_000) + " min.</b><br>" + _this.tr("Ali želite nadaljevati z delom?");

        _this.saopMessageDlg.onYes = _this.refreshSession.bind(_this);
        _this.saopMessageDlg.onNo = _this.proceedNo.bind(_this);
        _this.saopMessageDlg.Show(message, title, 1, [SaopMsgDlgButton.mbYes, SaopMsgDlgButton.mbNo], true, true);
 }

    showExpired(): void {
        global.ConsoleLogDegug("SaopSession.showWarning");
        let _this = this;

        let title = _this.tr("Vaša seja je potekla");

        let message = _this.tr("Za nadaljevanje se je potrebno ponovno prijaviti.") + "<br>" + _this.tr("Preusmerjeni boste na prijavno stran.");
    
        _this.saopMessageDlg.onOk = _this.proceedNo.bind(_this);
        _this.saopMessageDlg.Show(message, title, 1, [ SaopMsgDlgButton.mbOK], true, true);
    }


    proceedNo(): void {
        global.ConsoleLogDegug("SaopSession.ProceedNo");
        
        const doRedirect = () => {
            const returnUrl = window.location.pathname + window.location.search;
            window.location.href = getSiteAbsoluteURL("Account/Login") + "?ReturnUrl=" + encodeURIComponent(returnUrl);
        };

        this._saopServicesCore.afterDone = function(){
             doRedirect();
        } 
        this._saopServicesCore.ExecuteAction({
            action: "Account/Logout",
            type: "GET"
        });    
    }

}
